/**
 * Xero Sync Dashboard — Track 9
 *
 * Protected route: beforeLoad auth guard, SSR-safe.
 * Uses server functions only for Xero/CRM data.
 */

import { useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useAuth } from "../../../../components/AuthProvider";
import { createRouteError } from "../../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../../components/RouteSkeleton";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import {
  useXeroSyncStatus,
  useXeroSyncLog,
  useXeroSyncConflicts,
  useResolveXeroSyncConflict,
  useSyncXeroToCrm,
  type XeroSyncConflictRow,
  type XeroSyncLogRow,
} from "../../../../hooks/useXero";

export const Route = createFileRoute("/_admin/integrations/xero/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Xero Sync"),
  component: XeroSyncPage,
});

function XeroSyncPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <XeroSyncDashboard
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

const auth = (userId: string, accessToken: string) => ({
  userId,
  accessToken,
});

function XeroSyncDashboard({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const a = auth(userId, accessToken);
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useXeroSyncStatus(a);
  const { data: logsData, isLoading: logsLoading } = useXeroSyncLog(a, 20);
  const { data: conflictsData, isLoading: conflictsLoading, refetch: refetchConflicts } = useXeroSyncConflicts(a, 10);
  const conflicts: XeroSyncConflictRow[] = conflictsData ?? [];
  const logs: XeroSyncLogRow[] = logsData ?? [];
  const resolveConflict = useResolveXeroSyncConflict(a);
  const syncMutation = useSyncXeroToCrm(a);

  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [syncDirection, setSyncDirection] = useState<"xero_to_crm" | "crm_to_xero" | "bidirectional">("bidirectional");
  const [syncMessage, setSyncMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const handleResolve = (conflictId: string, resolution: "use_xero" | "use_crm" | "manual_merge") => {
    resolveConflict.mutate({ conflictId, resolution }, {
      onSuccess: () => refetchConflicts(),
    });
  };

  if (statsLoading && !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2">
          <span className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
          Loading sync dashboard…
        </div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          {statsError.message}
        </div>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-muted-foreground hover:text-foreground">
          ← Dashboard
        </Link>
      </div>
    );
  }

  return (
      <main className="container mx-auto p-6 space-y-6 max-w-4xl">
        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total Mappings"
              value={stats.total_mappings}
              sub={`${stats.synced_mappings} synced`}
            />
            <StatCard
              label="Pending"
              value={stats.pending_mappings}
              sub="awaiting sync"
            />
            <StatCard
              label="Conflicts"
              value={stats.conflict_mappings}
              sub="need review"
              highlight={stats.conflict_mappings > 0}
            />
            <StatCard
              label="Recent Errors"
              value={stats.recent_errors}
              sub="last 24h"
              highlight={stats.recent_errors > 0}
            />
          </div>
        )}

        {stats?.last_sync_time && (
          <p className="text-sm text-muted-foreground">
            Last sync: {new Date(stats.last_sync_time).toLocaleString()}
          </p>
        )}

        {/* Quick action: manual sync by entity */}
        <div className="rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold text-card-foreground mb-3">Quick Actions</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSyncMessage(null);
              const type = entityType.trim();
              const id = entityId.trim();
              if (!type || !id) {
                setSyncMessage({ type: "error", text: "Enter both entity type and entity ID." });
                return;
              }
              syncMutation.mutate(
                { entityType: type, entityId: id, syncDirection },
                {
                  onSuccess: (data) => {
                    const msg = data && typeof data === "object" && "message" in data ? (data as { message?: string }).message : "Sync triggered.";
                    setSyncMessage({ type: "success", text: msg ?? "Sync triggered." });
                    setEntityType("");
                    setEntityId("");
                    refetchStats();
                    refetchConflicts();
                  },
                  onError: (err) => {
                    setSyncMessage({ type: "error", text: err instanceof Error ? err.message : "Sync failed." });
                  },
                }
              );
            }}
            className="space-y-3"
          >
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Entity type</Label>
                <Input
                  type="text"
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                  list="entity-type-list"
                  placeholder="e.g. contact, invoice"
                  className="min-w-[140px]"
                />
                <datalist id="entity-type-list">
                  <option value="contact" />
                  <option value="invoice" />
                  <option value="account" />
                </datalist>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Entity ID</Label>
                <Input
                  type="text"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  placeholder="UUID or ID"
                  className="min-w-[200px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Direction</Label>
                <Select value={syncDirection} onValueChange={(v) => setSyncDirection(v as "xero_to_crm" | "crm_to_xero" | "bidirectional")}>
                  <SelectTrigger className="min-w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bidirectional">bidirectional</SelectItem>
                    <SelectItem value="xero_to_crm">xero_to_crm</SelectItem>
                    <SelectItem value="crm_to_xero">crm_to_xero</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={syncMutation.isPending}
                size="sm"
              >
                {syncMutation.isPending ? "Triggering…" : "Trigger manual sync"}
              </Button>
            </div>
            {syncMessage && (
              <p className={`text-sm ${syncMessage.type === "success" ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
                {syncMessage.text}
              </p>
            )}
          </form>
        </div>

        {/* Conflicts */}
        <div className="rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold text-card-foreground mb-3">Pending Conflicts ({conflicts.length})</h2>
          {conflictsLoading && conflicts.length === 0 ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : conflicts.length === 0 ? (
            <p className="text-muted-foreground text-sm">No conflicts.</p>
          ) : (
            <ul className="space-y-3">
              {conflicts.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 border rounded-md bg-background"
                >
                  <div>
                    <span className="text-sm font-medium text-foreground">{c.conflict_type}</span>
                    <span className="text-xs text-muted-foreground ml-2">Priority {c.priority}</span>
                    {c.conflicting_fields?.length ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.conflicting_fields.map((f) => (
                          <span key={f} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {f}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleResolve(c.id, "use_xero")}
                      disabled={resolveConflict.isPending}
                    >
                      Use Xero
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResolve(c.id, "use_crm")}
                      disabled={resolveConflict.isPending}
                    >
                      Use CRM
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResolve(c.id, "manual_merge")}
                      disabled={resolveConflict.isPending}
                    >
                      Manual
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Activity log */}
        <div className="rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold text-card-foreground mb-3">Recent Activity</h2>
          {logsLoading && logs.length === 0 ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No activity yet.</p>
          ) : (
            <ul className="space-y-2">
              {logs.map((log) => (
                <li
                  key={log.id}
                  className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0"
                >
                  <span className="text-foreground">{log.operation}</span>
                  <span className="text-muted-foreground">
                    {log.entity_type} • {log.entity_id}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
  );
}

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: number;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold ${highlight ? "text-destructive" : "text-foreground"}`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
